import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { AppDispatch, AppState } from '@nxsys/store';
import {
  FORM_STATUTORY_TYPE,
  LEAVE_DURATION_OPTION,
  STATUTORY_PAYMENT_TYPE,
} from '@nxsys/constants/statutory';
import { Descriptions, Form } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import FormStatutory from './FormStatutory';
import DetailHeader from './DetailHeader';
import { find } from 'lodash';
import { useDispatch } from 'react-redux';
import { statutoryActions } from '@nxsys/store/slices/statutory/reducer/statutorySlice';
import { GetAWEParams } from '@nxsys/store/slices/statutory/types';
import { getAWEStatutory } from '@nxsys/store/slices/statutory/action';
import { useEffect, useMemo, useState } from 'react';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';

const GeneralInformation = ({
  isEditing = false,
  toggleEditing,
}: {
  isEditing: boolean;
  toggleEditing: () => void;
}) => {
  const { messages } = useTypedIntl();
  const { type } = useParams();
  const { statutoryDetail } = useSelector(
    (state: AppState) => state.statutoryStore,
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const [aweData, setAweData] = useState<any>(null);
  const location = useLocation();
  const pathname = location.pathname;

  const [form] = Form.useForm();

  const FORM_DESCRIPTIONS: any = useMemo(() => {
    // Build left (type-specific) and right (AWE/pay) columns, then interleave
    const leftItems: any[] = [];
    const rightItems: any[] = [];

    // Left column: type-specific fields
    if (type === STATUTORY_PAYMENT_TYPE.SSP) {
      leftItems.push(
        {
          label: messages['statutory.form.piwStartDate'],
          value: statutoryDetail?.startDate
            ? moment(statutoryDetail?.startDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '',
        },
        {
          label: messages['statutory.form.piwEndDate'],
          value: statutoryDetail?.endDate
            ? moment(statutoryDetail?.endDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '',
        },
      );
    }

    if (
      type === STATUTORY_PAYMENT_TYPE.SMP ||
      type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
    ) {
      leftItems.push({
        label: messages['statutory.form.expectedWeekOfChildbirth'],
        value: statutoryDetail?.inputData?.expectedChildBirthWeek
          ? moment(
              statutoryDetail?.inputData?.expectedChildBirthWeek,
              'YYYY-MM-DD',
            ).format('DD/MM/YYYY')
          : '',
      });
      if (type === STATUTORY_PAYMENT_TYPE.SMP) {
        leftItems.push(
          {
            label: messages['statutory.form.notifiedLeaveDate'],
            value: statutoryDetail?.inputData?.notifiedLeaveStart
              ? moment(
                  statutoryDetail?.inputData?.notifiedLeaveStart,
                  'YYYY-MM-DD',
                ).format('DD/MM/YYYY')
              : '',
          },
          {
            label: messages['statutory.form.mppStartDate'],
            value:
              statutoryDetail?.inputData?.mppStartDate ||
              statutoryDetail?.inputData?.notifiedLeaveStart
                ? moment(
                    statutoryDetail?.inputData?.mppStartDate ||
                      statutoryDetail?.inputData?.notifiedLeaveStart,
                    'YYYY-MM-DD',
                  ).format('DD/MM/YYYY')
                : '',
          },
        );
      }
      if (type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH) {
        leftItems.push({
          label: messages['statutory.form.notifiedLeaveStart'],
          value: statutoryDetail?.inputData?.notifiedLeaveStart
            ? moment(
                statutoryDetail?.inputData?.notifiedLeaveStart,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        });
      }
      leftItems.push({
        label: messages['statutory.form.babyBornDate'],
        value: statutoryDetail?.inputData?.bornDate
          ? moment(
              statutoryDetail?.inputData?.bornDate,
              'YYYY-MM-DD',
            ).format('DD/MM/YYYY')
          : '',
      });
      if (type === STATUTORY_PAYMENT_TYPE.SMP) {
        leftItems.push({
          label:
            messages['statutory.form.pregnancyRelatedIllnessStartedOn'],
          value: statutoryDetail?.inputData?.illnessStartOn
            ? moment(
                statutoryDetail?.inputData?.illnessStartOn,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        });
      }
    }

    if (
      type === STATUTORY_PAYMENT_TYPE.SAP ||
      type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
    ) {
      leftItems.push(
        {
          label: messages['statutory.form.matchNotifiedDate'],
          value: statutoryDetail?.inputData?.matchNotifiedDate
            ? moment(
                statutoryDetail?.inputData?.matchNotifiedDate,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        },
        {
          label: messages['statutory.form.expectedPlacementDate'],
          value: statutoryDetail?.inputData?.expectedPlacementDate
            ? moment(
                statutoryDetail?.inputData?.expectedPlacementDate,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        },
      );
      if (type === STATUTORY_PAYMENT_TYPE.SAP) {
        leftItems.push({
          label: messages['statutory.form.startDateOfSAP'],
          value: statutoryDetail?.inputData?.startDate
            ? moment(
                statutoryDetail?.inputData?.startDate,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        });
      }
      if (type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION) {
        leftItems.push({
          label: messages['statutory.form.startDateOfSPP'],
          value: statutoryDetail?.inputData?.startDate
            ? moment(
                statutoryDetail?.inputData?.startDate,
                'YYYY-MM-DD',
              ).format('DD/MM/YYYY')
            : '',
        });
      }
      leftItems.push({
        label: messages['statutory.form.actualPlacementDate'],
        value: statutoryDetail?.inputData?.actualPlacementDate
          ? moment(
              statutoryDetail?.inputData?.actualPlacementDate,
              'YYYY-MM-DD',
            ).format('DD/MM/YYYY')
          : '',
      });
    }

    if (
      type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION ||
      type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
    ) {
      leftItems.push({
        label: messages['statutory.form.howLongPaternityLeave'],
        value:
          find(
            LEAVE_DURATION_OPTION,
            (item) =>
              item.value === statutoryDetail?.inputData?.leaveDuration,
          )?.label ?? '',
      });
    }

    // Right column: AWE/pay fields (all types except SSP)
    if (type !== STATUTORY_PAYMENT_TYPE.SSP) {
      // Match Week Date first for SAP/SPPA
      if (
        type === STATUTORY_PAYMENT_TYPE.SAP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
      ) {
        rightItems.push({
          label: messages['statutory.form.matchWeekDate'],
          value: statutoryDetail?.inputData?.matchWeekDate
            ? moment(statutoryDetail?.inputData?.matchWeekDate)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : '',
        });
      }
      rightItems.push({
        label: messages['statutory.form.eightWeekAverageDate'],
        value: `${moment(aweData?.fromDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')} - ${moment(aweData?.toDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
      });
      if (
        type === STATUTORY_PAYMENT_TYPE.SMP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
      ) {
        rightItems.push({
          label: messages['statutory.form.qualifyingWeek'],
          value: `${moment(aweData?.qualifyingWeek?.fromDate)
            .tz('Europe/London')
            .format('DD/MM/YYYY')} - ${moment(
            aweData?.qualifyingWeek?.toDate,
          ).format('DD/MM/YYYY')}`,
        });
      }
      rightItems.push(
        {
          label: messages['statutory.form.averagePay'],
          value: aweData?.awe,
        },
        {
          label: messages['statutory.form.averagePayManual'],
          value: statutoryDetail?.inputData?.averagePayManual,
        },
      );
    }

    // Interleave: left[0], right[0], left[1], right[1], ...
    const result: any[] = [];
    const maxLen = Math.max(leftItems.length, rightItems.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < leftItems.length) result.push(leftItems[i]);
      if (i < rightItems.length) result.push(rightItems[i]);
    }
    return result;
  }, [aweData]);

  const renderInfo = () => {
    return (
      <AppDescription
        column={2}
        labelStyle={{ width: 216 }}
        bordered={false}
        colon={false}
        size="middle"
      >
        {FORM_DESCRIPTIONS.map((des: any) => (
          <Descriptions.Item label={des.label}>{des.value}</Descriptions.Item>
        ))}
        {type === STATUTORY_PAYMENT_TYPE.SSP && (
          <Descriptions.Item label={messages['statutory.form.qualifyingDays']}>
            <div>
              {statutoryDetail?.settingQualifyingDays?.map((day: string) => (
                <AppTagStatus className={`Pending`}>
                  {day.charAt(0) + day.slice(1).toLowerCase()}
                </AppTagStatus>
              ))}
            </div>
          </Descriptions.Item>
        )}
      </AppDescription>
    );
  };

  const handleUpdate = async (params: any) => {
    const res = await dispatch(
      statutoryActions.updateStatutoryRecordById({
        type,
        id: statutoryDetail?.id,
        ...params,
      }),
    ).unwrap();
    const newPathName = pathname.replace(
      pathname.split('/').pop() ?? '',
      res?.id ?? '',
    );
    navigate(newPathName);
    toggleEditing();
  };

  const handleGetAWE = async (params: GetAWEParams) => {
    const res = await dispatch(getAWEStatutory(params)).unwrap();
    setAweData(res);
  };

  useEffect(() => {
    if (!statutoryDetail?.subcontractor?.id) return;
    if (
      (type === STATUTORY_PAYMENT_TYPE.SMP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH) &&
      statutoryDetail?.inputData?.expectedChildBirthWeek
    ) {
      handleGetAWE({
        type,
        subcontractorId: statutoryDetail.subcontractor.id,
        date: statutoryDetail.inputData.expectedChildBirthWeek,
      });
    }
    if (
      (type === STATUTORY_PAYMENT_TYPE.SAP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION) &&
      statutoryDetail?.inputData?.matchNotifiedDate
    ) {
      handleGetAWE({
        type,
        subcontractorId: statutoryDetail.subcontractor.id,
        matchNotifiedDate: statutoryDetail.inputData.matchNotifiedDate,
      });
    }
  }, [statutoryDetail]);

  const renderForm = () => {
    return (
      <FormStatutory
        formRef={form}
        formType={FORM_STATUTORY_TYPE.UPDATE}
        type={type as STATUTORY_PAYMENT_TYPE}
        onSubmit={handleUpdate}
      />
    );
  };

  return (
    <>
      <DetailHeader
        isEditing={isEditing}
        toggleEditing={toggleEditing}
        onSave={() => form.submit()}
      />
      <AppCard className="p-3">
        <h5 className="mb-4">
          {messages['statutory.form.generalInformation']}
        </h5>
        {!isEditing && renderInfo()}
        {isEditing && renderForm()}
      </AppCard>
    </>
  );
};

export default GeneralInformation;
